/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component, TemplateRef } from '@angular/core';
import { BaseComponent } from '../index';
import { DrawerService } from '../../services';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-drawer',
    templateUrl: './drawer.component.html',
    animations: [
        trigger('showHide', [
            state('show', style({
                    width: '{{drawerWidth}}px',
                }), { params: { drawerWidth: 0, transitionTiming: 0 } }
            ),
            state('hide',
                style({
                    width: '0px',
                })
            ),
            transition('hide => show',
                animate('{{transitionTiming}}s ease-in')
            ),
            transition('show => hide',
                animate('{{transitionTiming}}s ease-out')
            ),
        ])
    ],
    styleUrls: ['./drawer.component.scss']
})

export class DrawerComponent extends BaseComponent implements AfterViewInit {
    /**
     * Drawer width
     */
    public width: number = 345; // SCSS variable = $drawer-width - we cant use a shared variable :-(

    /**
     * Animation timing
     */
    public transitionTiming: number = 0.5;

    /**
     * Drawer title
     */
    public title: string;

    /**
     * Action dot content
     */
    public actionDots: TemplateRef<any>;

    /**
     * Drawer content
     */
    public content: string;

    /**
     * Draw visible state.
     */
    public drawVisible: boolean;

    /**
     * Pass in a template reference.
     */
    public template: any;

    /**
     * Pass in a template reference.
     */
    public data: any;

    /**
     * Animation complete state
     */
    public animationComplete: boolean = false;

    /**
     * Is loading flag.
     */
    public isLoading: boolean = false;

    constructor(
        private drawerService: DrawerService
    ) {
        super();

        this.setUpSubscribers();
        const drawerState = this.drawerService.drawerState;
        this.setClassVariables(drawerState);
    }

    public ngAfterViewInit() {
        this.drawerService.setTransitionTimings(this.transitionTiming);
        this.drawerService.setWidth(this.width);
    }

    /**
     * Returns the drawer visible state.
     * * @returns {boolean}
     */
    public isDrawerVisible(): boolean {
        return this.drawerService.getDrawerVisibility();
    }

    /**
     * Closes the drawer
     */
    public hide(): void {
        this.drawerService.close();
    }

    /**
     * Show hide scrollbar after/before animation.
     * @param $event
     */
    public showHideShadow($event: any): void {
        if ($event.fromState !== 'void') {
            if ($event.phaseName === 'done' && $event.toState === 'hide') {
                this.animationComplete = false;
            }
            if ($event.phaseName === 'start' && $event.toState === 'show') {
                this.animationComplete = true;
            }
        }
    }

    /**
     * Set up service subscribers.
     * @private
     */
    private setUpSubscribers(): void {
        this.drawerService.contentEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (drawerState: any): void => {
                    this.setClassVariables(drawerState);
                }
            });

        this.drawerService.loadingEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (isLoading: boolean): void => {
                    this.isLoading = isLoading;
                }
            });
    }

    /**
     * Set class variables.
     * @param drawerState
     * @private
     */
    private setClassVariables(drawerState: any): void {
        this.title = drawerState.drawContent.title;
        this.actionDots = drawerState.drawContent.actionDots;
        this.content = drawerState.drawContent.content;
        this.drawVisible = drawerState.visibility;
        this.template = drawerState.drawContent.template;
        this.data = drawerState.drawContent.data;
    }
}
